import { z } from 'zod';
import { Instant, Locale, PageQuery, Phone, TenantSlug, TimeZone, Uuid } from './common.js';
// Контур входа в КАБИНЕТ САЛОНА: вход, второй фактор, сессия, выбор активного салона,
// текущий пользователь с правами, приглашения сотрудников и управление доступами.
//
// Три правила, из которых выведено всё остальное в этом файле.
//
// 1. ПАРОЛЬ ЕСТЬ ТОЛЬКО ВО ВХОДЯЩИХ СХЕМАХ — при входе, смене и восстановлении.
//    Ни одна схема ответа его не содержит и содержать не может: в базе лежит argon2id-хеш
//    (ADR-024), отдавать наружу нечего, а поле «password» в ответе — это приглашение
//    залогировать его в APM вместе с телом ответа.
// 2. ЧЕЛОВЕК ОДИН, САЛОНОВ У НЕГО НЕСКОЛЬКО. platform_users — глобальная таблица,
//    членство живёт в tenant_members. Мастер на два барбершопа и бухгалтер на сеть —
//    норма, а не краевой случай, поэтому список салонов приходит прямо в ответе на вход,
//    а активный салон выбирается отдельным действием и хранится в сессии.
// 3. ПРАВА ПРИХОДЯТ СПИСКОМ, А НЕ ВЫВОДЯТСЯ ИЗ НАЗВАНИЯ РОЛИ. Салон переименовывает роли
//    и заводит свои, матрица прав редактируется владельцем (ADR-024, deny-by-default).
//    Интерфейс, который рисует меню по строке role === 'admin', сломается на первой же
//    кастомной роли и покажет человеку раздел, которого ему не положено.
// ───────────────────────── права ─────────────────────────
/**
 * Код атомарного права вида `раздел.сущность.действие`.
 *
 * Схема НАРОЧНО открытая (шаблон строки, а не z.enum): набор прав расширяется на сервере
 * вместе с разделами кабинета, а старый клиент обязан пережить незнакомое право молча —
 * при закрытом перечислении он уронил бы разбор всего ответа /me и человек не вошёл бы
 * в кабинет из-за права, которым он даже не пользуется.
 */
export const PermissionCode = z
    .string()
    .min(3)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*){1,3}$/, 'ожидается код вида clients.phone.view');
/**
 * Каталог прав, известных на момент сборки. Нужен интерфейсу для типизированных проверок
 * (`has('clients.export')` вместо строкового литерала в тридцати местах) и редактору ролей,
 * который обязан показывать права словами, а не машинными кодами.
 *
 * Права, помеченные в ADR-024 и в ТЗ кабинета как критичные, собраны рядом намеренно:
 * clients.list.view, clients.phone.view, clients.export, finance.view, billing.manage,
 * appointments.past.edit и discount.grant.above_limit по умолчанию выключены у роли master.
 * Мастер — самый вероятный будущий сотрудник конкурента, и база клиентов уходит
 * не через взлом, а через легальный доступ (ст. 272.1 УК).
 */
export const CABINET_PERMISSIONS = [
    // журнал
    'schedule.view',
    'schedule.export',
    'appointments.create',
    'appointments.reschedule',
    'appointments.cancel',
    'appointments.status.set',
    'appointments.past.edit',
    'busy.manage',
    'holds.manage',
    'discount.grant',
    'discount.grant.above_limit',
    // клиенты
    'clients.list.view',
    'clients.card.view',
    'clients.phone.view',
    'clients.create',
    'clients.edit',
    'clients.notes.view',
    'clients.notes.edit',
    'clients.blacklist.manage',
    'clients.merge',
    'clients.export',
    'clients.erase',
    'clients.consents.manage',
    // каталог и справочники
    'catalog.view',
    'catalog.services.manage',
    'catalog.resources.manage',
    'catalog.prices.manage',
    // сотрудники и графики
    'staff.view',
    'staff.manage',
    'staff.schedule.manage',
    'staff.invite',
    'roles.manage',
    // филиалы и правила
    'branches.view',
    'branches.manage',
    'booking_rules.manage',
    // деньги, отчёты, каналы, разработчикам, настройки
    'finance.view',
    'billing.manage',
    'reports.view',
    'channels.manage',
    /*
     * МАТРИЦА УВЕДОМЛЕНИЙ — ОТДЕЛЬНОЕ ПРАВО, А НЕ `channels.manage`.
     *
     * `channels.manage` есть у администратора, и выдан он ему ради одной кнопки —
     * «отправить ещё раз» тому, кто говорит «мне ничего не пришло». Матрица решает другое:
     * пойдёт ли напоминание по SMS, то есть 3,50–8,00 ₽ за каждое сообщение каждому
     * клиенту. Это деньги салона, и по ТЗ (4-Б5) экран адресован владельцу и управляющему.
     * Одно право на два разных разговора означало бы, что администратор за стойкой
     * включает салону платный канал, не имея ни одного денежного права.
     */
    'notifications.rules.manage',
    'templates.manage',
    'api_keys.manage',
    'webhooks.manage',
    'settings.view',
    'audit.view',
];
/** Системные роли салона. Дословно повторяют enum tenant_role_code. */
export const TenantRoleCode = z.enum([
    'owner',
    'manager',
    'admin',
    'master',
    'accountant',
    'marketer',
    'viewer',
]);
/** Состояние участия человека в салоне — enum tenant_member_status. */
export const TenantMemberStatus = z.enum(['invited', 'active', 'suspended', 'removed']);
// ───────────────────────── вход ─────────────────────────
/**
 * Пароль. Верхняя граница не косметическая: без неё запрос с мегабайтной строкой
 * заставит сервер прогнать по ней argon2id — дешёвый способ выжечь CPU на входе.
 */
export const CabinetPassword = z.string().min(10).max(200);
/**
 * Вход по e-mail и паролю.
 *
 * Адрес приводит к нижнему регистру СЕРВЕР, а не клиент: иначе один и тот же человек
 * заведёт вторую учётку регистром букв, и она будет выглядеть как чужая.
 * captchaToken появляется только после того, как антифрод заподозрил перебор (ADR-024):
 * показывать капчу всем на входе — это платить скоростью администратора за редкую атаку.
 */
export const CabinetLoginRequest = z.object({
    email: z.email().max(254),
    password: CabinetPassword,
    captchaToken: z.string().max(4096).optional(),
    // Подпись устройства в списке «мои устройства»: без неё владелец не поймёт,
    // какой из пяти активных входов отзывать при увольнении сотрудника.
    deviceLabel: z.string().trim().max(120).optional(),
});
/** Чем можно подтвердить второй фактор. Оба варианта нужны: телефон теряют. */
export const MfaMethod = z.enum(['totp', 'recovery']);
/**
 * Промежуточное состояние «пароль верный, нужен второй фактор».
 *
 * mfaToken — короткоживущий одноразовый билет ровно на подтверждение фактора: он не даёт
 * доступа к данным салона и потому не является сессией. Отдельный токен вместо «дожать
 * пароль ещё раз» нужен, чтобы пароль не путешествовал по сети дважды за один вход.
 */
export const CabinetMfaChallenge = z.object({
    mfaToken: z.string().min(32).max(512),
    methods: z.array(MfaMethod).min(1),
    expiresAt: Instant,
});
/**
 * Подтверждение второго фактора.
 *
 * trustDevice — осознанный компромисс: администратор за стойкой не должен вводить код
 * каждое утро, иначе он запишет секрет на бумажке под клавиатурой. У владельца доверие
 * устройству срок имеет всегда, «навсегда» тут не бывает.
 */
export const CabinetMfaVerifyRequest = z.object({
    mfaToken: z.string().min(32).max(512),
    // TOTP — шесть цифр, код восстановления длиннее; поэтому общая строка, а не regex на 6.
    code: z.string().trim().min(6).max(64),
    method: MfaMethod.default('totp'),
    trustDevice: z.boolean().default(false),
    deviceLabel: z.string().trim().max(120).optional(),
});
/**
 * «Пароль верный, фактор обязателен, завести его негде» — билет ровно на заведение.
 *
 * Отдельный билет, а не сессия: пускать внутрь салона человека без второго фактора
 * нельзя — требование роли ровно про это. Этим билетом можно только выпустить секрет
 * и подтвердить его кодом, и живёт он те же пять минут, что билет на ввод кода.
 */
export const CabinetMfaEnrollment = z.object({
    enrollToken: z.string().min(32).max(512),
    expiresAt: Instant,
});
/**
 * Выпущенный секрет: строка для QR и она же цифрами.
 *
 * СЕКРЕТ УХОДИТ НАРУЖУ ОДИН РАЗ И ТОЛЬКО ЗДЕСЬ. Прочитать его повторно нельзя ни одним
 * маршрутом: в базе лежит шифротекст (ADR-025), а расшифровка существует ради проверки
 * кода, а не ради показа. Потерявший — начинает заведение заново.
 *
 * secret нужен рядом с QR обязательно: телефон с камерой есть не у всех, а вводить
 * секрет руками — штатный путь в любом аутентификаторе.
 */
export const CabinetMfaSecret = z.object({
    account: z.string().min(1).max(320),
    secret: z.string().min(16).max(64),
    otpauthUri: z.string().min(1).max(1024),
});
/** Подтверждение заведения кодом из приложения. */
export const CabinetMfaConfirmRequest = z.object({
    code: z.string().trim().min(6).max(64),
});
/**
 * ПОВТОРНОЕ ПОДТВЕРЖДЕНИЕ ВТОРЫМ ФАКТОРОМ ПОСРЕДИ СЕАНСА (ТЗ 01, окно подтверждения).
 *
 * Выгрузка базы, обезличивание клиента, ключ доступа к данным требуют фактора, введённого
 * в последние 15 минут. Отметка живёт только в access-токене и намеренно не продлевается
 * его обновлением — поэтому подтвердить её можно было, только выйдя и войдя заново.
 * Этот запрос проверяет код и выдаёт НОВЫЙ access-токен той же сессии со свежей отметкой;
 * refresh, сессия и устройство не меняются.
 */
export const CabinetMfaReconfirmRequest = z.object({
    code: z.string().trim().min(6).max(64),
    method: MfaMethod.default('totp'),
});
export const CabinetMfaReconfirmResult = z.object({
    accessToken: z.string(),
    expiresAt: Instant,
    /** Момент подтверждения: действует 15 минут. */
    twoFactorAt: Instant,
});
/** То же, но при входе: билет вместо сессии. */
export const CabinetMfaEnrollConfirmRequest = CabinetMfaConfirmRequest.extend({
    enrollToken: z.string().min(32).max(512),
    deviceLabel: z.string().trim().max(120).optional(),
});
/**
 * Начать заведение ИЗ КАБИНЕТА.
 *
 * КОД ОБЯЗАТЕЛЕН, ЕСЛИ ФАКТОР УЖЕ ВКЛЮЧЁН, и это не формальность. Без него достаточно
 * было живой сессии: вкладка кабинета, оставленная открытой на стойке салона, позволяла
 * кому угодно завести СВОЙ фактор — прежний при этом гаснет вместе со всеми десятью
 * резервными кодами, и владелец учётки на следующем входе получает запрос кода, которого
 * у него нет. Тихий угон учётки, следов которого человек не видит.
 *
 * Когда фактора нет, поле не нужно: доказывать владение нечем, и защищает пароль,
 * которым человек только что вошёл.
 */
export const CabinetMfaSetupRequest = z
    .object({
    code: z.string().trim().min(6).max(64).optional(),
    method: MfaMethod.default('totp'),
})
    /*
     * ПУСТОГО ТЕЛА ДОСТАТОЧНО, когда фактора ещё нет: доказывать владение нечем, и требовать
     * при этом объект в теле значило бы отвечать «некорректные данные запроса» человеку,
     * который просто нажал «Включить». Значение по умолчанию задано целиком, а не пустым
     * объектом: метод обязателен на выходе схемы.
     */
    .default({ method: 'totp' });
/** Начать заведение при входе: билетом, без сессии. */
export const CabinetMfaEnrollRequest = z.object({
    enrollToken: z.string().min(32).max(512),
});
/**
 * Коды восстановления. Показываются РОВНО ОДИН РАЗ — в ответе на подтверждение
 * и на перевыпуск. В базе только хеши, и отдать их второй раз мы физически не можем;
 * это не ограничение интерфейса, а свойство хранения.
 */
export const CabinetMfaRecoveryCodes = z.object({
    recoveryCodes: z.array(z.string().min(8).max(64)).min(1),
});
/** Действие, требующее доказать владение фактором: выключение и перевыпуск кодов. */
export const CabinetMfaCodeRequest = z.object({
    code: z.string().trim().min(6).max(64),
    method: MfaMethod.default('totp'),
});
/**
 * Состояние второго фактора для экрана безопасности.
 *
 * recoveryLeft нужен обязательно: коды кончаются молча, и человек узнаёт об этом
 * в тот единственный день, когда они ему понадобились.
 */
export const CabinetMfaStatus = z.object({
    enabled: z.boolean(),
    /** Требует ли фактор роль или сама учётка. Если да — выключить его нельзя. */
    required: z.boolean(),
    /**
     * Кто требует: название салона или «ваша учётная запись». null — никто.
     * Без этого «выключить нельзя» читается как поломка, а не как правило салона.
     */
    requiredBy: z.string().max(200).nullable(),
    recoveryLeft: z.number().int().min(0),
    /** Начатое и неподтверждённое заведение: экран продолжает его, а не начинает заново. */
    pending: z.boolean(),
});
/**
 * Пара токенов доступа.
 *
 * accessToken живёт 15 минут и хранится клиентом В ПАМЯТИ вкладки, не в localStorage
 * (ADR-024): XSS не должен уносить долгоживущий доступ. refreshToken в браузерном контуре
 * НЕ отдаётся телом ответа вовсе — он уходит httpOnly/SameSite-cookie, которую скрипт
 * не прочитает. Поле здесь заполняется ТОЛЬКО для нативного приложения персонала:
 * cookie у него нет, и другого места хранить refresh тоже нет.
 */
export const CabinetTokenPair = z.object({
    accessToken: z.string().min(20),
    expiresAt: Instant,
    refreshToken: z.string().min(20).nullable(),
});
/** Профиль вошедшего человека. Это данные ПЛАТФОРМЫ (мы — оператор), а не салона. */
export const CabinetUserProfile = z.object({
    id: Uuid,
    displayName: z.string(),
    email: z.email().nullable(),
    phoneE164: Phone.nullable(),
    locale: Locale,
    // Второй фактор уже заведён. Если он требуется ролью, но не заведён, кабинет обязан
    // увести человека в мастер настройки, а не пустить внутрь (ADR-024).
    mfaEnabled: z.boolean(),
    mfaRequired: z.boolean(),
    lastLoginAt: Instant.nullable(),
    /** Когда пароль меняли последний раз — «Изменён 2 июня» в профиле (1-Э1.10). */
    passwordChangedAt: Instant.nullable(),
});
/**
 * Салон в списке «где я работаю».
 *
 * Здесь же видны непринятые приглашения (status = invited) — экран выбора салона это
 * единственное место, где человек вообще может на них наткнуться, если письмо потерялось.
 * Статус самого салона отдаётся рядом: в заблокированный за неуплату салон вход есть,
 * но кабинет показывает экран блокировки, и решать это надо до перехода, а не после.
 */
export const CabinetTenantMembership = z.object({
    tenantId: Uuid,
    tenantSlug: TenantSlug,
    tenantName: z.string(),
    tenantStatus: z.enum(['trial', 'active', 'grace', 'suspended', 'archived']),
    memberId: Uuid,
    memberStatus: TenantMemberStatus,
    roleId: Uuid,
    roleCode: TenantRoleCode.nullable(),
    roleName: z.string(),
    // Требование второго фактора задаётся ролью в конкретном салоне: человек может быть
    // мастером в одном салоне и владельцем в другом, и во второй вход строже.
    requireMfa: z.boolean(),
    // Пустой массив = доступны все филиалы салона, включая будущие. Это не «нет доступа»,
    // и путать эти два смысла нельзя — на экране это пишется словами.
    branchIds: z.array(Uuid),
    lastEnteredAt: Instant.nullable(),
});
/**
 * Успешный вход: кто вошёл, куда он может войти и чем ходить.
 *
 * activeTenantId пуст, когда салонов больше одного и человек ещё не выбрал: сервер не
 * угадывает за него салон — записать клиента не в тот салон хуже, чем показать лишний экран.
 * При единственном членстве сервер выбирает его сам, и экрана выбора не возникает.
 */
export const CabinetAuthenticated = z.object({
    user: CabinetUserProfile,
    memberships: z.array(CabinetTenantMembership),
    activeTenantId: Uuid.nullable(),
    tokens: CabinetTokenPair,
});
/**
 * Ответ на вход. Два исхода различаются полем status, а не наличием/отсутствием полей:
 * клиенту нужно однозначно выбрать ветку, а не гадать по undefined.
 */
export const CabinetLoginResponse = z.discriminatedUnion('status', [
    z.object({ status: z.literal('authenticated'), result: CabinetAuthenticated }),
    z.object({ status: z.literal('mfa_required'), challenge: CabinetMfaChallenge }),
    /*
     * ТРЕТИЙ ИСХОД: фактор роль требует, а у человека его нет.
     *
     * Раньше здесь был отказ mfa_not_enrolled со словами «настройте по ссылке из письма».
     * Ссылки такой не существует, и заведения фактора тоже не существовало: человек,
     * которому владелец включил требование, оставался снаружи насовсем — пароль верный,
     * а дальше идти некуда. Исход обязан быть ходом, а не тупиком.
     */
    z.object({ status: z.literal('mfa_enrollment_required'), enrollment: CabinetMfaEnrollment }),
]);
/**
 * Результат подтверждения фактора при ВХОДЕ: коды показаны и человек уже внутри.
 *
 * Сессия выдаётся сразу, без повторного входа: пароль проверялся пять минут назад,
 * код только что сошёлся — требовать пароль ещё раз значит просить его трижды за один
 * вход. login здесь всегда authenticated: другие исходы на этом шаге невозможны.
 */
export const CabinetMfaEnrollmentResult = z.object({
    recoveryCodes: z.array(z.string().min(8).max(64)).min(1),
    login: CabinetLoginResponse,
});
/**
 * Выбор активного салона. Меняет контекст сессии целиком: другой tenant_id в RLS,
 * другая роль, другие права, другие филиалы. Поэтому это отдельное действие с ответом
 * в виде полного контекста, а не заголовок, который клиент подставляет к каждому запросу:
 * заголовок легко забыть в одном месте, и запрос уйдёт в чужой салон.
 */
export const SelectTenantRequest = z.object({ tenantId: Uuid });
/**
 * Обновление сессии. Тело пустое в браузере — refresh лежит в httpOnly-cookie и уезжает сам.
 * Поле нужно нативному приложению персонала, у которого cookie нет.
 *
 * Сервер РОТИРУЕТ refresh при каждом использовании и отзывает всё семейство сессий,
 * если предъявлен уже отработавший токен (ADR-024): это и есть обнаружение кражи.
 */
export const RefreshSessionRequest = z.object({
    refreshToken: z.string().min(20).max(512).optional(),
});
/**
 * Выход. allDevices — отдельный и заметный сценарий: «я входил с чужого компьютера
 * в торговом центре». Без него человек выйдет на своём телефоне и будет считать,
 * что закрыл доступ везде.
 */
export const LogoutRequest = z.object({ allDevices: z.boolean().default(false) });
/**
 * Смена пароля. Текущий пароль обязателен даже внутри живой сессии: захваченная вкладка
 * не должна уметь сменить пароль и тем самым выбросить настоящего владельца из системы.
 * Успешная смена отзывает остальные сессии (password_changed) — это решение сервера,
 * в контракте оно отражено отсутствием каких-либо опций «оставить входы».
 */
export const ChangePasswordRequest = z.object({
    currentPassword: CabinetPassword,
    newPassword: CabinetPassword,
});
/**
 * Запрос восстановления доступа. Ответ всегда одинаковый, независимо от того, есть такой
 * адрес или нет: разный ответ превращает форму в бесплатный перечислитель учёток салона.
 */
export const RequestPasswordResetRequest = z.object({
    email: z.email().max(254),
    captchaToken: z.string().max(4096).optional(),
});
/** Завершение восстановления по одноразовой ссылке из письма. */
export const ConfirmPasswordResetRequest = z.object({
    // Не меньше 128 бит энтропии в hex/base64url — ссылка уходит в почту и живёт в истории.
    token: z.string().min(32).max(512),
    newPassword: CabinetPassword,
});
// ───────────────────────── текущий пользователь ─────────────────────────
/** Филиал, доступный человеку. Зона — обязательное поле: журнал рисуется в ней. */
export const CabinetBranchAccess = z.object({
    id: Uuid,
    name: z.string(),
    timezone: TimeZone,
    isActive: z.boolean(),
    // Шаг сетки нужен оболочке до открытия журнала: по нему считается высота строки.
    slotStepMinutes: z.number().int().positive(),
});
/**
 * Роль в выбранном салоне.
 *
 * restrictToOwnRecords — не право, а фильтр, и он меняет интерфейс сильнее любого права:
 * человек с ним видит в журнале только свои визиты, а чужое время — обезличенным «занято».
 * Держать этот признак отдельно от списка прав обязательно: он применяется и на сервере
 * (фильтр по employee_id), и в RLS, а не только прячет кнопки.
 */
export const CabinetRoleView = z.object({
    /** NULL у сеанса поддержки: роли салона у нашего сотрудника нет. */
    id: Uuid.nullable(),
    code: TenantRoleCode.nullable(),
    name: z.string(),
    isSystem: z.boolean(),
    restrictToOwnRecords: z.boolean(),
    requireMfa: z.boolean(),
});
/**
 * Баннер входа поддержки под салоном. Салон обязан видеть, что в его данных сейчас
 * находится сотрудник платформы, и по какому тикету (ADR-024, 152-ФЗ).
 */
export const CabinetImpersonationBanner = z.object({
    supportCaseId: Uuid,
    staffDisplayName: z.string(),
    reason: z.string(),
    readOnly: z.boolean(),
    startedAt: Instant,
    expiresAt: Instant,
});
/**
 * Полный контекст кабинета — ответ на «кто я и что мне можно» (экран Э1.6).
 *
 * Отдаётся одним запросом намеренно: оболочка не имеет права нарисовать навигацию раньше,
 * чем узнает права, иначе человека выбросит с его собственного раздела при первом рендере
 * (ADR-012, правило маршрутизации кабинета).
 */
export const CabinetContext = z.object({
    user: CabinetUserProfile,
    tenant: z.object({
        id: Uuid,
        slug: TenantSlug,
        name: z.string(),
        status: z.enum(['trial', 'active', 'grace', 'suspended', 'archived']),
        defaultLocale: Locale,
        defaultTimezone: TimeZone,
    }),
    /**
     * Членство в салоне. NULL У СЕАНСА ПОДДЕРЖКИ.
     *
     * Наш сотрудник в салоне не работает: строки в tenant_members у него нет и появиться
     * не должна (бриф админки П-Г1 запрещает вход «как сотрудник салона» прямо). Раньше
     * это поле было обязательным, и сеанс поддержки не мог получить контекст ВООБЩЕ —
     * кабинет отвечал ему «У вас нет доступа к этому салону», то есть режим поддержки
     * не работал, хотя полоса для салона была написана и ждала его.
     */
    memberId: Uuid.nullable(),
    memberStatus: TenantMemberStatus,
    role: CabinetRoleView,
    /**
     * Филиалы, доступные ЭТОМУ человеку, уже с учётом branch_scope. Пустого случая «все»
     * здесь нет: сервер разворачивает пустой scope в реальный список, чтобы клиент никогда
     * не решал, что означает пустота, — администратор точки на Ленина не должен увидеть
     * ни одной строки точки на Гагарина даже по ошибке интерфейса.
     */
    branches: z.array(CabinetBranchAccess),
    /** Филиал, выбранный в прошлый раз на этом устройстве; клиент вправе его переспросить. */
    defaultBranchId: Uuid.nullable(),
    /** Явный список прав. Меню, кнопки и колонки рисуются по нему и только по нему. */
    permissions: z.array(PermissionCode),
    /**
     * Карточка сотрудника, соответствующая этому пользователю. Пусто у администратора,
     * который сам не исполнитель. Для роли с restrictToOwnRecords это ключ ко всему журналу:
     * без него клиент не знает, какая колонка «своя».
     */
    employeeId: Uuid.nullable(),
    /**
     * Момент последнего подтверждения вторым фактором. Операции с массивом ПД (выгрузка базы)
     * требуют свежего подтверждения, а не «когда-то входил» — срок свежести решает сервер,
     * клиенту он нужен только чтобы заранее показать шаг подтверждения, а не отказ после.
     */
    twoFactorAt: Instant.nullable(),
    impersonation: CabinetImpersonationBanner.nullable(),
});
// ───────────────────────── приглашения сотрудников ─────────────────────────
/**
 * Срок жизни приглашения.
 *
 * ЗАЧЕМ ПОЛЕ ВООБЩЕ ЕСТЬ И ПОЧЕМУ У НЕГО НЕТ ЗНАЧЕНИЯ «БЕССРОЧНО». Приглашение — это
 * действующая ссылка на вход в чужой салон, лежащая в почте или в мессенджере. Живущее
 * вечно приглашение переживает и увольнение того, кого звали, и утечку его почты, и смену
 * владельца салона; всплывёт оно через год как чужой доступ к базе клиентов, и объяснить,
 * кто его выдал, будет уже некому. Поэтому срок обязателен, ограничен сверху неделей
 * и по умолчанию равен трём суткам: этого хватает выйти на работу в понедельник,
 * а протухшее приглашение отправляется повторно одной кнопкой.
 */
export const INVITATION_TTL_HOURS_DEFAULT = 72;
export const INVITATION_TTL_HOURS_MAX = 168;
/** Состояние приглашения. `expired` считает сервер по времени, а не отдельная джоба. */
export const InvitationStatus = z.enum(['pending', 'accepted', 'revoked', 'expired']);
/**
 * Создание приглашения.
 *
 * Адрес ИЛИ телефон — не оба обязательны, но хотя бы один нужен: приглашение, которое
 * некуда отправить, это просто занятое место в лимите тарифа.
 * Роль передаётся идентификатором, а не кодом: салон заводит собственные роли, и кода
 * у них нет вовсе.
 */
export const CreateInvitationRequest = z
    .object({
    email: z.email().max(254).optional(),
    phoneE164: Phone.optional(),
    roleId: Uuid,
    /**
     * Филиалы приглашаемого. ПУСТОЙ МАССИВ = все филиалы салона, включая те, что появятся
     * позже. Это сильное разрешение, и интерфейс обязан говорить о нём словами,
     * а не оставлять поле пустым по умолчанию.
     */
    branchIds: z.array(Uuid).max(100).default([]),
    /** Карточка сотрудника, к которой привязать учётку (мастер уже заведён в расписании). */
    employeeId: Uuid.optional(),
    expiresInHours: z
        .number()
        .int()
        .min(1)
        .max(INVITATION_TTL_HOURS_MAX)
        .default(INVITATION_TTL_HOURS_DEFAULT),
    /** Личная строка в письме: человек должен понять, кто и куда его зовёт. */
    message: z.string().trim().max(500).optional(),
})
    .refine((r) => r.email !== undefined || r.phoneE164 !== undefined, {
    error: 'нужен e-mail или телефон — приглашение должно куда-то уйти',
    path: ['email'],
});
/**
 * Приглашение в списке кабинета.
 *
 * ТОКЕНА ЗДЕСЬ НЕТ И НЕ БУДЕТ. В базе хранится только его хеш, сама ссылка существует
 * ровно один раз — в письме приглашённому. Иначе любой, кто видит список сотрудников,
 * входит в салон под чужим именем, а повторная отправка перестаёт что-либо значить.
 * Контакт показывается маской: список открыт и управляющему, и это чужой личный адрес.
 */
export const InvitationView = z.object({
    id: Uuid,
    status: InvitationStatus,
    emailMasked: z.string().nullable(),
    phoneMasked: z.string().nullable(),
    roleId: Uuid,
    roleName: z.string(),
    branchIds: z.array(Uuid),
    invitedByName: z.string().nullable(),
    createdAt: Instant,
    expiresAt: Instant,
    /** Когда ссылку отправляли в последний раз — по нему видно «висит пятый день». */
    lastSentAt: Instant.nullable(),
    acceptedAt: Instant.nullable(),
    revokedAt: Instant.nullable(),
});
/** Список приглашений. По умолчанию показываем только живые: разобранные — шум. */
export const ListInvitationsQuery = PageQuery.extend({
    status: InvitationStatus.optional(),
    roleId: Uuid.optional(),
    branchId: Uuid.optional(),
});
export const ListInvitationsResult = z.object({
    items: z.array(InvitationView),
    nextCursor: z.string().nullable(),
});
/**
 * Что видит человек по ссылке ДО того, как принял приглашение (экран Э1.5).
 *
 * Показываем салон, роль и приглашающего, но ни одной строки данных салона: по ссылке
 * приходит кто угодно, включая того, кому её переслали. Признак needsPassword отвечает
 * на единственный развилочный вопрос экрана: заводить вход или человек уже есть у нас.
 */
export const InvitationPreview = z.object({
    tenantName: z.string(),
    tenantSlug: TenantSlug,
    roleName: z.string(),
    /**
     * Код системной роли — по нему экран берёт описание одной фразой (SYSTEM_ROLES.summary).
     * По названию подбирать нельзя: салон переименовывает роли. null — роль салона своя,
     * и фразы для неё у нас нет.
     */
    roleCode: TenantRoleCode.nullable(),
    /** Роль требует второго фактора: без него человек не войдёт, и сказать это надо заранее. */
    roleRequiresMfa: z.boolean(),
    /** Названия филиалов приглашения. Пустой список = все филиалы салона, включая будущие. */
    branchNames: z.array(z.string()),
    invitedByName: z.string().nullable(),
    /** Когда выписано: «Приглашал … 2 сентября» на экране просроченного. */
    invitedAt: Instant,
    expiresAt: Instant,
    /** Когда принято: «Вы приняли его 3 сентября. Просто войдите». */
    acceptedAt: Instant.nullable(),
    status: InvitationStatus,
    needsPassword: z.boolean(),
    /**
     * Приглашение пришло только на телефон, а вход в кабинет — по e-mail: без адреса
     * новая учётка получилась бы такой, в которую нельзя войти.
     */
    needsEmail: z.boolean(),
    /**
     * У адресата уже есть учётная запись с паролем. Привязать её к салону может только
     * её владелец: экран ведёт на вход, а принятие идёт из-под него
     * (POST invitations/accept-signed-in). Иначе держатель ссылки — а её получает и
     * приглашающий — привязывал бы к своему салону чужую учётку без её пароля.
     */
    signInRequired: z.boolean(),
});
/**
 * Принятие приглашения по токену.
 *
 * Пароль здесь задаёт сам приглашённый и только если учётки ещё нет: мы не придумываем
 * пароль за человека и не пересылаем его никому — иначе первый же пароль уедет в почту
 * администратора и останется там навсегда.
 */
/**
 * Редакции документов, которые сотрудник принимает при вступлении в салон.
 *
 * Сервер пишет в журнал (staff_consents) ту редакцию, что действует сейчас, — клиент её
 * не присылает: подсунуть старую редакцию запросом нельзя. Смена текста документа =
 * новая строка здесь, и все последующие согласия ссылаются уже на неё.
 */
export const STAFF_CONSENT_VERSIONS = {
    terms: 'draft-2026-09',
    staffPersonalData: 'draft-2026-09',
};
/**
 * Два РАЗДЕЛЬНЫХ согласия (экран 1-Э1.5): условия использования и обработка данных самого
 * сотрудника. Оба обязательны — literal(true), а не boolean: «не согласен» здесь означает
 * «не вступаю», и запрос без отметок до сервиса не доходит.
 */
export const StaffConsentAcceptance = z.object({
    terms: z.literal(true),
    staffPersonalData: z.literal(true),
});
export const AcceptInvitationRequest = z
    .object({
    token: z.string().min(32).max(512),
    consents: StaffConsentAcceptance,
    /** Нужны только новому человеку; у существующего берутся из его учётки. */
    displayName: z.string().trim().min(1).max(200).optional(),
    password: CabinetPassword.optional(),
    locale: Locale.optional(),
    /** Только для приглашения по телефону без e-mail (InvitationPreview.needsEmail). */
    email: z.email().max(254).optional(),
})
    .refine((r) => r.password === undefined || r.displayName !== undefined, {
    error: 'для новой учётной записи нужно имя',
    path: ['displayName'],
});
/**
 * Принятие приглашения уже вошедшим человеком — для того, у кого учётка есть.
 * Салон и роль берутся из приглашения, личность — из сессии; адресат приглашения
 * обязан совпасть с учётной записью сессии.
 */
export const AcceptInvitationSignedInRequest = z.object({
    token: z.string().min(32).max(512),
    consents: StaffConsentAcceptance,
});
/**
 * Приглашение, адресованное вошедшему человеку, — строка на экране выбора салона (1-Э1.4):
 * «Вас пригласила Ольга Ким. Роль: мастер — …» и «Принять приглашение» без ссылки.
 * Адресат определяется так же, как при принятии по ссылке: по каналу, на который оно ушло.
 */
export const MyInvitation = z.object({
    invitationId: Uuid,
    tenantId: Uuid,
    tenantName: z.string(),
    roleName: z.string(),
    roleCode: TenantRoleCode.nullable(),
    roleRequiresMfa: z.boolean(),
    branchNames: z.array(z.string()),
    invitedByName: z.string().nullable(),
    invitedAt: Instant,
    expiresAt: Instant,
});
export const ListMyInvitationsResult = z.object({ items: z.array(MyInvitation) });
/** Принятие из списка салонов: идентификатор вместо токена, согласия — те же два. */
export const AcceptMyInvitationRequest = z.object({
    invitationId: Uuid,
    consents: StaffConsentAcceptance,
});
/**
 * Отзыв приглашения. Причина не обязательна, но попадает в аудит: «кто дал этому человеку
 * доступ» и «кто передумал» — вопросы одного разбора инцидента.
 */
export const RevokeInvitationRequest = z.object({
    invitationId: Uuid,
    reason: z.string().trim().max(300).optional(),
});
/**
 * Роль салона в выборе «кого приглашаем» (окно приглашения на «Сотрудниках»).
 *
 * `grantable` считает сервер тем же правилом, что проверяет выпуск приглашения
 * (role-grant.ts): роль сильнее своей интерфейс показывает, но выбрать не даёт — и говорит
 * почему, а не прячет её, чтобы человек не гадал, куда делся «Управляющий».
 */
export const CabinetRoleOption = z.object({
    id: Uuid,
    code: TenantRoleCode.nullable(),
    name: z.string(),
    isSystem: z.boolean(),
    restrictToOwnRecords: z.boolean(),
    requireMfa: z.boolean(),
    grantable: z.boolean(),
    notGrantableReason: z.string().nullable(),
});
export const ListCabinetRolesResult = z.object({ items: z.array(CabinetRoleOption) });
/**
 * РОЛЬ В РЕДАКТОРЕ (4-Б11): права целиком, флажки, сколько на ней людей и правил ли её салон.
 * Отдельно от CabinetRoleOption: тот нужен окну приглашения (кому что можно выдать),
 * этот — владельцу, который правит сами роли.
 */
export const CabinetRoleEditorView = z.object({
    id: Uuid,
    code: TenantRoleCode.nullable(),
    name: z.string(),
    isSystem: z.boolean(),
    permissions: z.array(z.string()),
    restrictToOwnRecords: z.boolean(),
    requireMfa: z.boolean(),
    /** Люди с этой ролью, кроме уволенных: «На роли „Ресепшен“ 2 человека». */
    membersCount: z.number().int().nonnegative(),
    /** Системная роль отличается от умолчания (имя, права или флажки) — «роль изменена салоном». */
    modified: z.boolean(),
    /** Роль владельца: права и «только свои записи» не меняются — иначе владелец запрёт сам себя. */
    locked: z.boolean(),
    updatedAt: Instant,
});
export const ListRoleEditorResult = z.object({ items: z.array(CabinetRoleEditorView) });
const RoleName = z.string().trim().min(2, 'Назовите роль').max(60);
/** «Создать роль — копией»: новая роль салона с правами и флажками образца. */
export const CopyRoleRequest = z.object({
    sourceRoleId: Uuid,
    name: RoleName,
});
/** «Сохранить роль». Версия обязательна: два окна редактора не затирают друг друга молча. */
export const UpdateRoleRequest = z.object({
    roleId: Uuid,
    name: RoleName,
    permissions: z.array(z.enum(CABINET_PERMISSIONS)).max(CABINET_PERMISSIONS.length),
    restrictToOwnRecords: z.boolean(),
    requireMfa: z.boolean(),
    expectedUpdatedAt: Instant,
});
/** Удаление своей роли салона. Системную и роль с людьми — нельзя. */
export const DeleteRoleRequest = z.object({
    roleId: Uuid,
    expectedUpdatedAt: Instant,
});
/**
 * Повторная отправка. Сервер выпускает НОВЫЙ токен и гасит прежний: иначе после
 * трёх «отправить ещё раз» в почте живут три работающие двери в салон.
 */
export const ResendInvitationRequest = z.object({
    invitationId: Uuid,
    expiresInHours: z
        .number()
        .int()
        .min(1)
        .max(INVITATION_TTL_HOURS_MAX)
        .default(INVITATION_TTL_HOURS_DEFAULT),
});
// ───────────────────────── сотрудники с доступом ─────────────────────────
/**
 * Человек с доступом в кабинет. Это ДРУГОЙ список, чем сотрудники в расписании:
 * мастер может работать и не иметь входа вовсе, а бухгалтер — иметь вход и не быть
 * ни в одной колонке журнала. Путать их — значит закрыть доступ, не закрыв смену,
 * или наоборот.
 */
export const CabinetMemberView = z.object({
    memberId: Uuid,
    userId: Uuid,
    displayName: z.string(),
    emailMasked: z.string().nullable(),
    phoneMasked: z.string().nullable(),
    roleId: Uuid,
    roleCode: TenantRoleCode.nullable(),
    roleName: z.string(),
    branchIds: z.array(Uuid),
    status: TenantMemberStatus,
    mfaEnabled: z.boolean(),
    employeeId: Uuid.nullable(),
    /** Главная колонка списка: по последнему входу видны забытые учётки бывших сотрудников. */
    lastLoginAt: Instant.nullable(),
    acceptedAt: Instant.nullable(),
    removedAt: Instant.nullable(),
    /**
     * Кто закрыл доступ (ТЗ 04, Б10: карточка уволенного — «видно, когда и кто закрыл»).
     * null — доступ не закрыт или закрыт до появления этой отметки.
     */
    removedByName: z.string().nullable(),
    /** Роль сужена до своих записей — «Мастер · только свои записи» в карточке. */
    roleOwnRecordsOnly: z.boolean(),
    /** Роль требует второй фактор; mfaEnabled — заведён ли он у самого человека. */
    roleRequiresMfa: z.boolean(),
    /** Снимок версии для проверки конфликта при правке доступа (см. expectedUpdatedAt). */
    updatedAt: Instant,
});
export const ListMembersQuery = PageQuery.extend({
    search: z.string().trim().max(120).optional(),
    status: TenantMemberStatus.optional(),
    roleId: Uuid.optional(),
    branchId: Uuid.optional(),
});
export const ListMembersResult = z.object({
    items: z.array(CabinetMemberView),
    nextCursor: z.string().nullable(),
});
/**
 * Изменение роли и филиалов сотрудника.
 *
 * expectedUpdatedAt — защита от тихого затирания: пока управляющий менял филиалы,
 * владелец мог сменить роль тому же человеку, и «последний победил» здесь означает
 * незаметно восстановленный доступ к базе клиентов.
 */
export const UpdateMemberAccessRequest = z.object({
    memberId: Uuid,
    roleId: Uuid.optional(),
    branchIds: z.array(Uuid).max(100).optional(),
    employeeId: Uuid.nullable().optional(),
    expectedUpdatedAt: Instant,
});
/**
 * Приостановка доступа — отпуск, разбирательство. Отдельно от увольнения намеренно:
 * это обратимое действие, и путать его с закрытием доступа нельзя ни в интерфейсе,
 * ни в контракте.
 */
export const SuspendMemberRequest = z.object({
    memberId: Uuid,
    reason: z.string().trim().max(300).optional(),
    expectedUpdatedAt: Instant,
});
export const RestoreMemberRequest = z.object({
    memberId: Uuid,
    expectedUpdatedAt: Instant,
});
/**
 * Закрытие доступа при увольнении. Человек не удаляется — строка остаётся ради аудита
 * «кто менял запись год назад», а история его действий и его записи никуда не деваются.
 * Сервер обязан немедленно отозвать все сессии этого человека В ЭТОМ салоне, включая
 * открытые на телефоне: увольнение, после которого вкладка продолжает работать, —
 * это не увольнение.
 */
export const RevokeMemberAccessRequest = z.object({
    memberId: Uuid,
    reason: z.string().trim().max(300).optional(),
    expectedUpdatedAt: Instant,
});
/**
 * Активный вход в списке «мои устройства» и в карточке сотрудника.
 * Ни токена, ни его хеша здесь нет — только приметы, по которым человек узнаёт устройство.
 */
export const CabinetDeviceSessionView = z.object({
    sessionId: Uuid,
    deviceLabel: z.string().nullable(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    createdAt: Instant,
    lastUsedAt: Instant.nullable(),
    expiresAt: Instant,
    /** Та сессия, из которой пришёл запрос: её нельзя закрыть «как чужую», не выйдя. */
    isCurrent: z.boolean(),
    /**
     * Сеанс поддержки платформы, открытый в ЭТОМ салоне.
     *
     * ПОЧЕМУ ОН ВООБЩЕ ЗДЕСЬ. Список сеансов — это ответ на вопрос «кто сейчас внутри».
     * Сеанс поддержки принадлежит нашему сотруднику, а не салону, поэтому в список «мои
     * устройства» он не попадал вовсе: салон видел баннер, пока оператор работал, и
     * не имел никакого следа после. Скрыть эту строку нельзя — ни фильтром, ни настройкой:
     * доступ к чужим персональным данным должен быть виден тому, чьи это данные (152-ФЗ).
     *
     * null — обычный сеанс самого человека.
     */
    support: z
        .object({
        /** Кто из наших сотрудников работает: имя, а не идентификатор. */
        operatorName: z.string(),
        /** От имени кого в салоне: «от имени администратора Ирины». */
        actsAsName: z.string().nullable(),
        /** Номер обращения: по нему салон найдёт переписку и спросит по делу. */
        supportCaseId: Uuid,
        /** Причина словами — та же, что в баннере. */
        reason: z.string(),
        startedAt: Instant,
        /** Жёсткий предел сеанса: салон должен знать, когда дверь закроется сама. */
        expiresAt: Instant,
        /** Только чтение или с правом менять: это разные разговоры. */
        readOnly: z.boolean(),
    })
        .nullable(),
});
/**
 * Смена языка интерфейса.
 *
 * ЯЗЫК — СВОЙСТВО ЧЕЛОВЕКА, А НЕ САЛОНА. Один и тот же администратор работает в двух
 * салонах, и язык, выбранный им в одном, обязан действовать в обоих: он не меняется
 * от того, чей кабинет открыт. Поэтому запись идёт в учётку, а не в членство.
 *
 * ЯЗЫК СООБЩЕНИЙ КЛИЕНТУ ЭТИМ НЕ МЕНЯЕТСЯ. Уведомления локализуются по языку
 * ПОЛУЧАТЕЛЯ (ADR-030), и выбор администратора на них не влияет — иначе клиент получил
 * бы напоминание на языке того, кто нажал кнопку.
 */
export const ChangeLocaleRequest = z.object({ locale: Locale });
export const RevokeSessionRequest = z.object({ sessionId: Uuid });
//# sourceMappingURL=cabinet-auth.js.map